import React, { useEffect, useState } from 'react';
import axios from '../../utils/axios';
import Sidebar from './sidebar';
import toast from 'react-hot-toast';
import resolveImageUrl from '../../utils/imageUtils';
import { useNavigate } from 'react-router-dom';
import {
  FaUsers,
  FaBuilding,
  FaSpinner,
  FaCheckCircle,
  FaUserShield,
  FaUserTie,
  FaExclamationCircle
} from 'react-icons/fa';

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalProperties: 0,
    inProcess: 0,
    resolved: 0,
    inspectors: 0,
    supervisors: 0,
    awaitingExplanations: 0,
  });

  // PIN search state (similar to supervisor dashboard)
  const [pinSearch, setPinSearch] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchedProperty, setSearchedProperty] = useState(null);
  const [searchMessage, setSearchMessage] = useState('');

  const navigate = useNavigate();

  const hasUsableValue = (value) => {
    if (value === null || value === undefined) return false;
    const str = String(value).trim().toLowerCase();
    return str !== '' && str !== 'null' && str !== 'undefined' && str !== 'n/a';
  };

  useEffect(() => {
    const fetchDashboardStats = async () => {
      try {
        const res = await axios.get('/users/dashboard-stats');
        setStats(res.data);
      } catch (err) {
        console.error(err);
        toast.error('Failed to load dashboard stats');
      }
    };

    fetchDashboardStats();
  }, []);

  // Search handler — searches all properties by PIN
  const handleSearchByPin = async () => {
    const pin = pinSearch.trim();
    if (!pin) {
      setSearchMessage('Please enter a PIN.');
      setSearchedProperty(null);
      return;
    }

    try {
      setSearching(true);
      setSearchMessage('');
      setSearchedProperty(null);

      const res = await axios.get('/properties', { params: { limit: 10000 } });
      const list = res.data?.data || res.data?.properties || [];
      const found = list.find(
        (p) => String(p.pin || '').trim().toLowerCase() === pin.toLowerCase()
      );

      if (!found) {
        setSearchMessage('No property found against this PIN.');
        return;
      }

      setSearchedProperty(found);
    } catch (err) {
      console.error(err);
      setSearchMessage('Failed to search property by PIN.');
    } finally {
      setSearching(false);
    }
  };

  const cardData = [
    //{ title: 'Total Users', value: stats.totalUsers, color: 'teal', icon: <FaUsers /> },
    { title: 'Total Properties', value: stats.totalProperties, color: 'blue', icon: <FaBuilding /> },
    //{ title: 'Properties In Process', value: stats.inProcess, color: 'yellow', icon: <FaSpinner /> },
    { title: 'Resolved Properties', value: stats.resolved, color: 'green', icon: <FaCheckCircle /> },
    { title: 'Inspectors', value: stats.inspectors, color: 'indigo', icon: <FaUserTie /> },
    { title: 'Supervisors', value: stats.supervisors, color: 'purple', icon: <FaUserShield /> },
    { title: 'Total Assigned Properties', value: stats.awaitingExplanations, color: 'rose', icon: <FaExclamationCircle /> },
  ];

  return (
    <div className="flex h-screen bg-gray-100">
      <Sidebar />
      <main className="flex-1 p-6 overflow-y-auto">
        <h1 className="text-3xl font-bold text-teal-700 mb-8">Admin Dashboard</h1>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {cardData.map((card, index) => (
            <Card key={index} {...card} />
          ))}
        </div>

        {/* PIN Search Bar (Admin) */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h2 className="text-lg font-semibold text-gray-800 mb-3">
            Search Property by PIN
          </h2>
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="text"
              value={pinSearch}
              onChange={(e) => setPinSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSearchByPin();
                }
              }}
              placeholder="Enter exact PIN..."
              className="flex-1 border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
            />
            <button
              onClick={handleSearchByPin}
              disabled={searching}
              className="px-5 py-2 rounded-lg bg-teal-600 text-white font-medium hover:bg-teal-700 disabled:opacity-60"
            >
              {searching ? 'Searching...' : 'Search'}
            </button>
          </div>

          {searchMessage && (
            <p className="mt-3 text-sm text-gray-600">{searchMessage}</p>
          )}

          {searchedProperty && (
            <div className="mt-4 border border-teal-200 rounded-lg p-4 bg-teal-50">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="w-full md:w-48 h-32 bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center">
                  {searchedProperty.image ? (
                    <img
                      src={resolveImageUrl(searchedProperty.image)}
                      alt="Property"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-sm text-gray-500">No Image</span>
                  )}
                </div>
                <div className="flex-1">
                  <p className="text-sm text-gray-700">
                    <span className="font-semibold">PIN:</span> {searchedProperty.pin}
                  </p>
                  <p className="text-sm text-gray-700">
                    <span className="font-semibold">Circle:</span>{' '}
                    {searchedProperty.circle || 'N/A'}
                  </p>
                  <p className="text-sm text-gray-700">
                    <span className="font-semibold">Location:</span>{' '}
                    {searchedProperty.location || 'N/A'}
                  </p>
                  <p className="text-sm text-gray-700">
                    <span className="font-semibold">Status:</span>{' '}
                    {searchedProperty.status || 'N/A'}
                  </p>
                  <p className="text-sm text-gray-700">
                    <span className="font-semibold">Land Area:</span>{' '}
                    {hasUsableValue(searchedProperty.landarea_admin)
                      ? searchedProperty.landarea_admin
                      : 'N/A'}
                  </p>
                  <p className="text-sm text-gray-700">
                    <span className="font-semibold">Covered Area:</span>{" "}
                    {hasUsableValue(searchedProperty.coveredarea_admin)
                      ? searchedProperty.coveredarea_admin
                      : "N/A"}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

const Card = ({ title, value, color, icon }) => {
  const colorMap = {
    teal: 'text-teal-600',
    blue: 'text-blue-600',
    yellow: 'text-yellow-500',
    green: 'text-green-500',
    indigo: 'text-indigo-500',
    purple: 'text-purple-500',
    rose: 'text-rose-500',
  };

  return (
    <div className="bg-white shadow-lg rounded-xl p-5 flex items-center space-x-4 hover:shadow-xl transition-shadow duration-300">
      <div className={`text-4xl ${colorMap[color]}`}>{icon}</div>
      <div>
        <h2 className="text-lg font-semibold text-gray-600">{title}</h2>
        <p className={`text-2xl font-bold ${colorMap[color]}`}>{value}</p>
      </div>
    </div>
  );
};

export default Dashboard;
